<template>
  <transition name="bundle-gallery-fade">
    <suspense>
      <ProductGallery
        v-if="route?.query?.bundleGallery && mounted && isNewBundleGallery"
        :active-media="activeMedia"
        :medias="medias"
        :notification="notification"
        @update:notification="notification = $event"
        @updateActiveMedia="updateActiveMedia"
        @close="goBack()"
        @toggleSnackbar="toggleSnackbar"
        @trackEvents="trackEvents($event)"
      ></ProductGallery>
    </suspense>
  </transition>
  <div class="product-set" :class="[`product-set-${set?.id}`]">
    <h2 class="header-default light new-pdp-title" v-if="titleOnTop && set && set.title">
      {{ set.title }}
    </h2>
    <div class="product-set-container">
      <div class="product-set-image">
        <Gallery
          v-if="!isNewBundleGallery"
          ref="gallery"
          :medias="medias"
          :width-ratio="widthRatio"
          :height-ratio="heightRatio"
          @trackEvents="trackEvents"
          @changeSlide="emitEvent('slideChange')"
        />
        <BundleSet
          v-if="isNewBundleGallery"
          :medias="medias"
          :active-media="activeMedia"
          @trackEvents="trackEvents"
          @updateActiveMedia="updateActiveMedia"
        >
        </BundleSet>
        <div>
          <slot name="body">
            <h2 class="header-default light" v-if="!titleOnTop && set && set.title">
              {{ set.title }}
            </h2>
            <div v-if="set && set.description" class="product-set-description">
              {{ set.description }}
            </div>
            <a v-if="shouldShowShopAllSubText && shopAllBundleSubText" class="accent" :href="shopAllBundleLink">{{ shopAllBundleSubText }}</a>
          </slot>
        </div>
      </div>
      <div class="product-set-list">
        <div class="products">
          <div v-for="(product, index) in products" :key="product.hashKey" class="product">
            <div class="product-image" @click="emitEvent('productClick')">
              <PreserveRatio :width="2600" :height="1349">
                <a :href="getProductUrl(getConfiguredProduct(index).activeVersion)" target="_blank">
                  <transition name="fade" mode="out-in">
                    <ResponsiveImage
                      :url="getConfiguredProduct(index).activeVersion.productImage"
                      :alt="getConfiguredProduct(index).activeVersion.title"
                      :widths="[92]"
                      :breakpoints="[]"
                      :skeleton="true"
                    ></ResponsiveImage>
                  </transition>
                </a>
              </PreserveRatio>
              <ProductBadge v-if="getConfiguredProduct(index).activeVersion.onSale" class="product-badge-custom" :type="'sale'" :size="'small'" />
              <ProductBadge
                v-else-if="getConfiguredProduct(index).activeVersion.onClearance"
                class="product-badge-custom"
                :type="'clearance'"
                :size="'small'"
              />
            </div>
            <div class="product-details">
              <div class="product-title">
                <a :href="getProductUrl(getConfiguredProduct(index).activeVersion)" target="_blank">
                  <template v-if="getConfiguredProduct(index).quantity > 1">{{ getConfiguredProduct(index).quantity }} x </template>
                  {{ getConfiguredProduct(index).activeVersion.title }}
                </a>
              </div>
              <div v-if="product.productThumbnails.length > 1" class="product-variations">
                <a
                  v-for="variation in product.productThumbnails"
                  :key="variation.hashKey"
                  class="product-variation"
                  :href="`/product/${variation.hashKey}/${variation.slug}`"
                  target="_blank"
                  :class="{ active: isVariationSelected(index, variation) }"
                  @mouseover="hover(variation)"
                  @click="(e) => select(e, index, variation)"
                  :aria-label="`${variation.title} Option`"
                >
                  <ResponsiveImage
                    :url="variation.variantImageUrl + '?fit=max&w={width}&q={q}'"
                    :widths="[28]"
                    :breakpoints="[]"
                    :skeleton="true"
                    :alt="`${variation.title}`"
                  ></ResponsiveImage>
                </a>
              </div>
              <div class="product-link">
                <a :href="getProductUrl(getConfiguredProduct(index).activeVersion)" target="_blank" class="secondary"> View Product </a>
              </div>
            </div>
            <div class="product-price">
              <span v-if="isConfigurationOnSaleClearance(index)" class="original-total">{{
                formatMoney(currency, getConfiguredOriginalPrice(index))
              }}</span>
              <span class="final-total bold accent">{{ formatMoney(currency, getConfiguredFinalPrice(index)) }}</span>
            </div>
          </div>
        </div>

        <div class="product-set-information">
          <ProductFinancing :amount-in-cents="finalPrice" />
        </div>

        <div class="product-set-pricing">
          <div v-if="saleDiscount" class="product-set-line">
            <div class="product-set-line-title">
              <template v-if="promotion">{{ promotion }}</template>
              <template v-else>Sale</template> Savings
            </div>
            <div class="product-set-line-price product-set-bundle-sale">–{{ formatMoney(currency, saleDiscount) }}</div>
          </div>
          <div v-if="clearanceDiscount" class="product-set-line">
            <div class="product-set-line-title">Clearance Savings</div>
            <div class="product-set-line-price product-set-bundle-sale">–{{ formatMoney(currency, clearanceDiscount) }}</div>
          </div>
          <div v-if="bundleDiscount" class="product-set-line">
            <div class="product-set-line-title">Bundle Discount</div>
            <div class="product-set-line-price">–{{ formatAndRoundDownMoney(currency, bundleDiscount) }}</div>
          </div>
          <div class="product-set-line">
            <div class="product-set-line-title">Subtotal</div>
            <div class="product-set-line-price">
              <span v-if="totalDiscount" class="original-total">{{ formatAndRoundDownMoney(currency, originalPrice) }}</span>
              <span class="final-total bold accent">{{ formatAndRoundUpMoney(currency, finalPrice) }}</span>
            </div>
          </div>
        </div>
        <CartAnimationPlaceholder></CartAnimationPlaceholder>
        <template v-if="showAddToCart">
          <AddToCartButton :products="productsParsed" :animate="isDesktopAndGreater" class="add-to-cart-button" @click="addToCart">
            Add Bundle to Cart
          </AddToCartButton>
        </template>
        <template v-else>
          <button @click="goToRoomBundles" class="shop-all-button" :aria-label="`Shop all ${primaryRoom} bundles`"> Shop All {{ primaryRoom }} Bundles</button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { PreserveRatio, MediaUtils } from '@/pixel/index';
import Gallery from '@/components/product/Gallery.vue';
import { ref, watch, computed } from 'vue';
import ResponsiveImage from '@/components/commons/ResponsiveImage.vue';
import AddToCartButton from '@/components/commons/AddToCartButton.vue';
import CartAnimationPlaceholder from '@/components/product/CartAnimationPlaceholder.vue';
import { getSessionCountry } from '@/components/commons/utils.js';
import { formatMoney, formatAndRoundDownMoney, formatAndRoundUpMoney } from '@/components/cart/utils.js';
import ProductBadge from '@/components/commons/ProductBadge.vue';
import LocationService from '@/services/LocationService.js';
import useCommon from '@/compositions/useCommon';
import ProductFinancing from '@/components/product/ProductFinancing.vue';
import useProductBundle from '@/compositions/useProductBundle.js';
import BundleSet from '@/components/sets/BundleSet.vue';
import ProductGallery from '@/components/product/ProductGallery.vue';
import { useRoute } from 'vue-router';
import Chat from '@/components/commons/Chat';
import useExperiment from '@/compositions/useExperiment.js';
import Tracking from '@/components/commons/Tracking.js';

export default {
  name: 'ProductSet',
  components: {
    ResponsiveImage,
    PreserveRatio,
    AddToCartButton,
    CartAnimationPlaceholder,
    Gallery,
    ProductBadge,
    ProductFinancing,
    BundleSet,
    ProductGallery,
  },
  props: {
    set: {
      type: Object,
      required: true,
    },
    promotion: {
      type: String,
      default: null,
    },
    showAddToCart: {
      type: Boolean,
      default: true,
    },
    primaryRoom: {
      type: String,
      default: null,
    },
    shouldShowShopAllSubText: {
      type: Boolean,
      default: false,
    },
    titleOnTop: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const { getProductBundlePrice } = useProductBundle();
    const { currentCountry } = useCommon();
    const originalPrice = ref(null);
    const bundleDiscount = ref(null);
    const saleDiscount = ref(null);
    const clearanceDiscount = ref(null);
    const totalDiscount = ref(null);
    const finalPrice = ref(null);
    const route = useRoute();
    const activeMedia = ref(parseInt(route?.query?.bundleGallery, 10) || 0);
    const { isFeatureVariant } = useExperiment();
    const isNewBundleGallery = computed(() => isFeatureVariant('new-bundle-gallery', 'on'));

    const getPrices = () => {
      const prices = getProductBundlePrice(props.set);
      originalPrice.value = prices.originalPrice;
      bundleDiscount.value = prices.bundleDiscount;
      saleDiscount.value = prices.saleDiscount;
      clearanceDiscount.value = prices.clearanceDiscount;
      totalDiscount.value = prices.totalDiscount;
      finalPrice.value = prices.finalPrice;
    };

    getPrices();
    watch(
      () => props.set,
      () => getPrices(),
    );

    function preventPinchZoom(event) {
      if (event.scale !== 1) {
        event.preventDefault();
      }
    }

    function updateGalleryDisplay(route) {
      const html = document.querySelector('html');
      html.classList.add('overflow-hidden');
      if (html && (route?.query.hasOwnProperty('gallery') || route?.query.hasOwnProperty('bundleGallery'))) {
        Chat.hideChatWindow();
        document.addEventListener('touchmove', preventPinchZoom, false);
      } else {
        html.classList.remove('overflow-hidden');
        Chat.defaultChatWindow();
        document.removeEventListener('touchmove', preventPinchZoom);
      }
    }

    watch(
      () => route?.query,
      () => {
        updateGalleryDisplay(route);
      },
    );

    return {
      originalPrice,
      bundleDiscount,
      saleDiscount,
      clearanceDiscount,
      totalDiscount,
      finalPrice,
      getPrices,
      currentCountry,
      activeMedia,
      route,
      isNewBundleGallery,
    };
  },

  data() {
    return {
      configuration: [],
      primaryProduct: {
        productImage: null,
      },
      mobile: MediaUtils.isMobileAndSmaller,
      mounted: false,
      notification: true,
    };
  },

  computed: {
    heroImages() {
      if (this.set?.fileKey) {
        return [this.getResponsiveImageSource(this.set.mobileFileKey, this.set.fileKey, this.set.title)];
      }
      return [];
    },

    floorplanImages() {
      if (this.set?.floorplanFileKey) {
        return [this.getResponsiveImageSource(this.set.floorplanMobileFileKey, this.set.floorplanFileKey, this.set.title)];
      }
      return [];
    },

    productImages() {
      return this.configuration.map((config) => ({
        alt: config.activeVersion.title,
        key: config.activeVersion.imageUrl,
        url: config.activeVersion.imageUrl,
      }));
    },
    productDimensionImages() {
      return this.configuration.map((config) => ({
        alt: config.activeVersion.title,
        key: config.activeVersion.imageUrl,
        url: config.activeVersion.dimensionImageUrl,
      }));
    },
    images() {
      return [...this.heroImages, ...this.productImages, ...this.productDimensionImages, ...this.floorplanImages];
    },

    medias() {
      return this.images
        .filter((i) => i)
        .map((media) => ({
          ...media,
          type: media.type || 'IMAGE',
          url: `${media.url}?fit=max&w={width}&q={q}`,
          thumbnailUrl: `${media.url}?fit=max&w={width}&q={q}`,
          zoomUrl: `${media.url}?fit=max&w={width}&q=50`,
          product: {},
        }));
    },

    products() {
      return this.set ? this.set.productBundleProducts : [];
    },

    currency() {
      const country = getSessionCountry();
      return country.currencySymbol;
    },

    productsParsed() {
      return this.configuration
        .map((config) => ({
          id: config.activeVersion.hashKey,
          quantity: config.quantity,
        }))
        .reverse();
    },

    isDesktopAndGreater() {
      return MediaUtils.isDesktopAndGreater;
    },

    shopAllBundleSubText() {
      return this.set?.productBundleRooms?.[0] ? `Shop all ${this.set.productBundleRooms[0].toLowerCase()} bundles` : '';
    },

    shopAllBundleLink() {
      return this.$router.resolve({
        name: 'sets',
        query: { 'filter.rooms': this.set.productBundleRooms[0] },
      }).href;
    },

    isUSA() {
      return LocationService.isUS();
    },

    widthRatio() {
      return this.mobile ? 400 : 2890;
    },
    heightRatio() {
      return this.mobile ? 472 : 1500;
    },
  },

  watch: {
    set: {
      immediate: true,
      handler(val) {
        if (val?.productBundleProducts?.length) {
          // eslint-disable-next-line prefer-destructuring
          this.primaryProduct = val.productBundleProducts[0];
          this.configuration = [...val.productBundleProducts];
        }
      },
    },
  },

  mounted() {
    this.mounted = true;
  },

  methods: {
    getResponsiveImageSource(mobileKey, desktopKey, title) {
      if (this.mobile && mobileKey) {
        return {
          alt: title,
          key: mobileKey,
          url: mobileKey,
        };
      }
      if (desktopKey) {
        return {
          alt: title,
          key: desktopKey,
          url: desktopKey,
        };
      }
      return null;
    },

    getVariations(product) {
      return product && product.variations ? [product, ...product.variations] : [product];
    },

    getConfiguredProduct(index) {
      return this.configuration[index];
    },

    getProductUrl({ hashKey, slug }) {
      return `/product/${hashKey}/${slug}`;
    },

    isConfigurationOnSaleClearance(index) {
      const product = this.getConfiguredProduct(index);
      return product.activeVersion.onClearance || product.activeVersion.onSale;
    },

    getConfiguredOriginalPrice(index) {
      const product = this.getConfiguredProduct(index);
      return this.getCountryPrice(product.activeVersion.price).price * product.quantity;
    },

    getConfiguredSalePrice(index) {
      const product = this.getConfiguredProduct(index);
      let salePrice;
      if (product.activeVersion.onClearance) {
        salePrice = this.getCountryPrice(product.activeVersion.price).clearance;
      } else if (product.activeVersion.onSale) {
        salePrice = this.getCountryPrice(product.activeVersion.price).sale;
      }
      return salePrice * product.quantity;
    },

    getConfiguredFinalPrice(index) {
      return this.isConfigurationOnSaleClearance(index) ? this.getConfiguredSalePrice(index) : this.getConfiguredOriginalPrice(index);
    },

    isVariationSelected(index, variation) {
      return this.configuration[index].activeVersion.hashKey === variation.hashKey;
    },

    hover() {},

    select(e, index, variation) {
      if (this.configuration[index].activeVersion.hashKey === variation.hashKey) {
        // Allow the default action of navigating to the url
      } else {
        e.preventDefault();
        if (index === 0) {
          this.primaryProduct = variation;
        }
        this.configuration[index].activeVersion = variation;
        this.configuration[index].activeVersion.productImage = `${this.configuration[index].activeVersion.imageUrl}?fit=max&w={width}&q={q}`;

        this.$refs.gallery.slideTo(index + this.heroImages.length);
        this.getPrices();
        // this.reloadDeliveryEstimate() // TODO: will need this for ETA
      }
    },

    formatMoney,

    goToRoomBundles() {
      window.location.href = this.shopAllBundleLink;
    },
    formatAndRoundDownMoney,
    formatAndRoundUpMoney,

    getCountryPrice(priceArray) {
      if (priceArray && Array.isArray(priceArray)) {
        return priceArray.find((p) => p.currencyCode.toLowerCase() == getSessionCountry().currency);
      }

      return {};
    },

    emitEvent(eventName) {
      this.$emit(eventName);
    },

    updateActiveMedia(index) {
      this.activeMedia = index;
    },

    goBack() {
      window.history.back();
    },
    toggleSnackbar() {
      this.notification = false;
    },

    trackEvents(event) {
      Tracking.track(event, { bundleId: this.set.id });
    },

    addToCart() {
      this.set.productBundleProducts.forEach((item) => {
        Tracking.track('Add To Cart', {
          productId: item?.activeVersion?.hashKey,
          skuNo: item?.activeVersion?.skuNo,
          price: LocationService.isUS() ? item?.activeVersion?.price[0]?.price / 100 : item?.activeVersion?.price[1]?.price / 100,
          name: item?.activeVersion?.title,
          currencyCode: LocationService.isUS() ? item?.activeVersion?.price[0]?.currencyCode : item?.activeVersion?.price[1]?.currencyCode,
          category: this.set?.productBundleRooms[0] + ' bundle',
        });
      });
      Tracking.track('Bundle_Add_to_Cart', { newGallery: this.isNewBundleGallery });
      this.$emit('addToCart');
    },
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

.product-set {
  width: 100%;

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 100ms ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .product-set-container {
    margin-top: var(--space-4);
    display: flex;

    @media (max-width: calc($tablet-breakpoint - 1px)) {
      flex-direction: column;
      margin-top: var(--space-4);
    }
  }

  .product-set-title {
    font-size: var(--font-size-24);
    line-height: var(--line-height-24);
    margin-bottom: var(--space-1);
  }

  .product-set-description {
    font-size: var(--font-size-16);
    line-height: var(--line-height-16);
    margin-top: var(--space-2);
    margin-bottom: var(--space-2);
  }

  .product-set-image {
    flex: 0 0 66%;
    width: 66%;

    .gallery {
      margin-bottom: var(--space-6);
    }

    @media (max-width: calc($tablet-breakpoint - 1px)) {
      flex: 0 0 auto;
      margin: auto;
      width: 100%;
    }
    @media (min-width: $tablet-breakpoint) {
      .gallery .activeImage {
        margin: 0;
      }
    }
  }

  .product-set-list {
    flex-grow: 1;
    margin-left: var(--space-4);

    .product-set-information {
      border-top: 1px solid var(--grey-light);
      border-bottom: 1px solid var(--grey-light);
      padding: var(--space-6) 0;
    }

    @media (max-width: calc($tablet-breakpoint - 1px)) {
      margin-top: var(--space-10);
    }

    @media (max-width: calc($desktop-breakpoint - 1px)) {
      margin-top: var(--space-4);
      margin-left: 0px;
    }
    @media (min-width: $desktop-breakpoint) {
      margin-left: var(--space-10);
    }
    @media (min-width: $tablet-breakpoint) and (max-width: calc($desktop-breakpoint - 1px)) {
      margin-left: var(--space-6);
    }
  }

  .products {
    margin-bottom: var(--space-6);
  }

  .product {
    display: flex;

    /* purgecss ignore */
    &:not(:first-child) {
      margin-top: var(--space-6);
    }
  }

  .product-image {
    flex: 0 0 92px;
    position: relative;
  }

  .product-badge-custom {
    left: calc(var(--space-3) * -1);
    top: calc(var(--space-2) * -1);
  }

  .product-set-line {
    display: flex;
    color: var(--grey-medium);
  }

  .product-title,
  .product-price,
  .product-set-line {
    font-size: var(--font-size-16);
    line-height: var(--line-height-16);
  }

  .product-price {
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    justify-content: flex-end;
  }

  .product-set-discount-amount {
    font-family: var(--font-bold);
  }

  .product-details {
    flex-grow: 1;
    margin-left: var(--space-2);
    margin-right: var(--space-2);
  }

  .product-variations {
    display: flex;
    flex-wrap: wrap;
    margin-top: var(--space-2);

    .product-variation {
      line-height: 1em;
      margin-right: 2px;

      /* purgecss ignore */
      &:hover {
        img {
          border: 3px solid var(--white);
          box-shadow: 0 0 0 1px var(--coral);
        }
      }

      &.active {
        a {
          z-index: 0;
          transition-delay: 0.1s;
        }
        img {
          border: 3px solid var(--white);
          box-shadow: 0 0 0 1px var(--coral);
        }
      }

      img {
        display: inline-block;
        width: 22px;
        height: 22px;
        border-radius: 15px;
        border: 3px solid var(--white);
        transition: border-color 100ms linear;
      }
    }

    .image.skeleton {
      border-radius: 50%;
      width: 22px;
      height: 22px;
      margin: 6px;
    }
  }

  .product-link {
    margin-top: var(--space-2);
  }

  .product-set-pricing {
    margin-top: var(--space-6);
    margin-bottom: var(--space-6);
  }

  .product-set-line-title {
    flex-grow: 1;
  }

  .original-total {
    text-decoration: line-through;
    color: var(--grey-medium);
  }

  .final-total {
    margin-left: var(--space-2);
  }

  .add-to-cart-button button {
    width: 100%;

    @media (max-width: calc($tablet-breakpoint - 1px)) {
      width: auto;
    }

    @media (max-width: calc($mobile-breakpoint - 1px)) {
      width: 100%;
    }
  }

  .shop-all-button {
    width: 100%;
    background: $white !important;
    color: $coral !important;

    /* purgecss ignore */
    &:hover {
      background: $coral !important;
      color: $white !important;
    }

    @media (max-width: calc($tablet-breakpoint - 1px)) {
      width: auto;
    }

    @media (max-width: calc($mobile-breakpoint - 1px)) {
      width: 100%;
    }
  }
}
</style>
